import React, { useMemo } from "react";

import HomeHeader from "./HomeHeader.jsx";
import ProjectSearch from "./ProjectSearch.jsx";
import ProjectCard from "./ProjectCard.jsx";

const getProjectId = (project) => {
  if (!project) return "";

  return String(
    project._id ??
      project.projectId ??
      project.projectID ??
      project.project_id ??
      project.projectCode ??
      project.code ??
      "",
  ).trim();
};

const getProjectKey = (project, index) => {
  const projectId = getProjectId(project);

  if (projectId) {
    return projectId;
  }

  const projectName = String(project?.projectName || "unnamed-project").trim();

  const company = String(project?.company || "unknown-company").trim();

  const location = String(project?.siteLocation || "unknown-location").trim();

  return `${projectName}-${company}-${location}-${index}`;
};

const ProjectSelectionView = ({
  userDetails,
  assignedProjects = [],
  filteredProjects = [],
  selectedProject = "",
  searchTerm = "",
  onSearchChange,
  onProjectSelect,
}) => {
  const hasProjects = assignedProjects.length > 0;

  // console.group("ProjectSelectionView Data");

  // console.log("userDetails:", userDetails);
  // console.log("assignedProjects:", assignedProjects);
  // console.log("filteredProjects:", filteredProjects);
  // console.log("selectedProject:", selectedProject);
  // console.log("searchTerm:", searchTerm);
  // console.log("hasProjects:", hasProjects);
  // console.log("onSearchChange:", onSearchChange);
  // console.log("onProjectSelect:", onProjectSelect);

  const sortedProjects = useMemo(() => {
    return [...filteredProjects].sort((projectA, projectB) => {
      const projectIdA = getProjectId(projectA);
      const projectIdB = getProjectId(projectB);

      return projectIdA.localeCompare(projectIdB, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
  }, [filteredProjects]);

  const uniqueProjects = useMemo(() => {
    const usedIdentifiers = new Set();

    return sortedProjects.filter((project, index) => {
      const projectId = getProjectId(project);

      /*
       * Projects with no database ID are still displayed,
       * but receive a generated rendering identifier.
       */
      const identifier =
        projectId ||
        [project?.projectName, project?.company, project?.siteLocation, index]
          .filter(Boolean)
          .join("|");

      if (usedIdentifiers.has(identifier)) {
        return false;
      }

      usedIdentifiers.add(identifier);
      return true;
    });
  }, [sortedProjects]);

  const hasFilteredProjects = uniqueProjects.length > 0;

  const handleProjectClick = (project) => {
    const projectId = getProjectId(project);

    if (!projectId) {
      console.error(
        "Project cannot be selected because it has no valid identifier:",
        project,
      );

      return;
    }

    onProjectSelect?.(projectId);
  };

  return (
    <>
      <HomeHeader userDetails={userDetails} />

      <div className="project-section">
        <div className="project-section-header">
          <h2 className="section-title">Your Projects</h2>

          {hasProjects && (
            <ProjectSearch value={searchTerm} onChange={onSearchChange} />
          )}
        </div>

        {!hasProjects ? (
          <div className="no-project-message">
            No projects are currently assigned to your account.
            <br />
            Please contact your administrator.
          </div>
        ) : !hasFilteredProjects ? (
          <div className="no-project-message">
            No projects match your search.
          </div>
        ) : (
          <div className="project-cards-container">
            <ProjectCard
              isPortfolio
              isActive={!selectedProject}
              onClick={() => onProjectSelect?.("")}
            />

            {uniqueProjects.map((project, index) => {
              const projectId = getProjectId(project);

              const projectKey = getProjectKey(project, index);

              return (
                <ProjectCard
                  key={projectKey}
                  project={project}
                  isActive={Boolean(projectId) && selectedProject === projectId}
                  onClick={() => handleProjectClick(project)}
                />
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default ProjectSelectionView;
